import { DEFAULT_PAGE, DEFAULT_PAGE_SIZE, IntegrationSource } from '@/constants';
import { queryKeys } from '@/hooks/queryKeys';
import { useGetCourseStudents } from '@/hooks/useGetCourseStudents';
import { useAppData } from '@/providers/AppDataProvider';
import { useLocale } from '@/providers/LocaleProvider';
import { useSnackbar } from '@/providers/SnackbarProvider';
import { ICourseStudent } from '@/types/views/student.types';
import { isEnrollmentTerminal } from '@/utilities';
import { openStudentCertificatePdf } from '@/utilities/openCertificatePdf';
import { Menu, MenuItem } from '@mui/material';
import { GridPaginationModel } from '@mui/x-data-grid-pro';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo, useRef, useState } from 'react';
import {
  CourseStudentRow,
  CourseStudentsDialogType,
  CourseStudentsTable,
} from '../table/CourseStudentsTable';
import { ConfirmUnenrolStudentDialog } from './ConfirmUnenrolStudentDialog';
import { Dialog } from './Dialog';
import { LearningHistoryDialog } from './LearningHistoryDialog';
import { SendReminderDialog } from './SendReminderDialog';
import { SetEnrolmentDateDialog, SetEnrolmentDateMode } from './SetEnrolmentDateDialog';

export type { CourseStudentRow, CourseStudentsDialogType };

const mapStudentToRow = (student: ICourseStudent): CourseStudentRow => ({
  // Server-supplied row key, not the employee id: a student who took this course more than once
  // returns one row per LearnUpon enrolment, which would collide in the grid's row map.
  id: student.rowKey,
  employeeId: student.employeeId,
  name: student.fullName,
  firstName: student.firstName,
  lastName: student.lastName,
  fullName: student.fullName,
  jobTitle: student.jobTitle ?? undefined,
  status: student.status,
  isOverdue: student.isOverdue,
  dueDate: student.dueDate ?? undefined,
  courseAccessExpiresAt: student.courseAccessExpiresAt ?? undefined,
  progress: student.percentageCompleted,
  enrolledCount: student.enrolled,
  completedCount: student.completed,
  inProgressCount: student.inProgress,
  notStartedCount: student.notStarted,
  failedCount: student.failed,
  overdueCount: student.overdue,
  pendingCount: student.pending,
  learnUponEnrollmentId: student.learnUponEnrollmentId,
  learnUponInstance: student.learnUponInstance,
  integrationId: student.integrationId,
  completedDate: student.completedDate ?? undefined,
  integrationSource: student.integrationSource,
  canSendReminder: student.canSendReminder,
  enrollable: student.enrollable,
  courseEnrollmentId: student.courseEnrollmentId ?? undefined,
  canUnenrol: student.canUnenrol,
  employeeTrainingId: student.employeeTrainingId ?? undefined,
  learningPathEnrollmentId: student.learningPathEnrollmentId ?? undefined,
  integrationEnrollmentId: student.integrationEnrollmentId ?? undefined,
  hasCertificate: student.hasCertificate,
  canSetDates: student.canSetDates,
});

const TITLE_KEYS: Record<CourseStudentsDialogType, string> = {
  enrolled: 'manage.courseStudents.enrolledTitle',
  completed: 'manage.courseStudents.completedTitle',
  inProgress: 'manage.courseStudents.inProgressTitle',
  overdue: 'manage.courseStudents.overdueTitle',
};

// In a dialog already scoped to one course, the column reads "Expiry" — the shared def's
// "Course expiry" wording is for course-agnostic tables like the Enrolments tab.
const expiryColumn = {
  type: CourseStudentsTable.columns.courseExpiry,
  label: 'manage.studentCourses.expiry',
};

const COLUMNS_BY_TYPE: Record<
  CourseStudentsDialogType,
  {
    type: (typeof CourseStudentsTable.columns)[keyof typeof CourseStudentsTable.columns];
    label?: string;
  }[]
> = {
  enrolled: [
    { type: CourseStudentsTable.columns.studentName },
    { type: CourseStudentsTable.columns.status },
    { type: CourseStudentsTable.columns.dueDate },
    expiryColumn,
    { type: CourseStudentsTable.columns.actions },
  ],
  completed: [
    { type: CourseStudentsTable.columns.studentName },
    { type: CourseStudentsTable.columns.completedDate },
    expiryColumn,
    { type: CourseStudentsTable.columns.actions },
  ],
  inProgress: [
    { type: CourseStudentsTable.columns.studentName },
    { type: CourseStudentsTable.columns.progress },
    { type: CourseStudentsTable.columns.dueDate },
    expiryColumn,
    { type: CourseStudentsTable.columns.actions },
  ],
  overdue: [
    { type: CourseStudentsTable.columns.studentName },
    { type: CourseStudentsTable.columns.progress },
    { type: CourseStudentsTable.columns.dueDate },
    expiryColumn,
    { type: CourseStudentsTable.columns.actions },
  ],
};

interface CourseStudentsDialogProps {
  open: boolean;
  onClose: () => void;
  type: CourseStudentsDialogType;
  courseId?: string;
  courseName?: string;
  theme?: any;
  onLearningHistory?: (row: CourseStudentRow) => void;
  onSendReminderSuccess?: (courseName?: string) => void;
}

export const CourseStudentsDialog = ({
  open,
  onClose,
  type,
  courseId,
  courseName,
  theme,
  onLearningHistory,
  onSendReminderSuccess,
}: CourseStudentsDialogProps) => {
  const { translate } = useLocale();
  const { user } = useAppData();
  const { showSnackbar } = useSnackbar();
  const queryClient = useQueryClient();

  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: DEFAULT_PAGE,
    pageSize: DEFAULT_PAGE_SIZE,
  });
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [unenrolRow, setUnenrolRow] = useState<CourseStudentRow | null>(null);
  const [historyRow, setHistoryRow] = useState<CourseStudentRow | null>(null);
  const [reminderRow, setReminderRow] = useState<CourseStudentRow | null>(null);
  const [dateDialog, setDateDialog] = useState<{
    mode: SetEnrolmentDateMode;
    row: CourseStudentRow;
  } | null>(null);
  const menuRow = useRef<CourseStudentRow | null>(null);

  const handleStudentAction = (e: React.MouseEvent, row: CourseStudentRow) => {
    menuRow.current = row;
    setMenuAnchor(e.currentTarget as HTMLElement);
  };

  const handleMenuClose = () => setMenuAnchor(null);

  const { data, isLoading } = useGetCourseStudents({
    courseId: open ? courseId : undefined,
    statusFilter: [type],
    paginationModel,
  });

  const students = useMemo(() => data?.students?.data?.map(mapStudentToRow) ?? [], [data]);

  const handlePaginationModelChange = useCallback(
    (model: GridPaginationModel) => {
      if (isLoading) return;
      setPaginationModel(model);
    },
    [isLoading]
  );

  const handleClose = () => {
    setPaginationModel({ page: DEFAULT_PAGE, pageSize: DEFAULT_PAGE_SIZE });
    onClose();
  };

  const isAllowEnrollActions =
    user?.isAllowEnrollStudents && menuRow.current?.integrationSource !== IntegrationSource.Manual;

  const isAllowUnenrollActions =
    user?.isAllowUnenrollStudents &&
    menuRow.current?.integrationSource !== IntegrationSource.Manual;

  const isTerminalStatus = isEnrollmentTerminal(menuRow.current?.status);
  const isAllowReminder =
    isAllowEnrollActions && !isTerminalStatus && menuRow.current?.canSendReminder;
  const isAllowUnenrol =
    isAllowUnenrollActions && !isTerminalStatus && !!menuRow.current?.canUnenrol;

  const showViewCertificate = !!menuRow.current?.hasCertificate;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      theme={theme}
      fullWidth
      title={translate(TITLE_KEYS[type])}
      maxWidth='md'
      closeButtonSx={{
        display: 'block',
      }}
      contentSx={{
        paddingBottom: { xs: '20px !important', md: '40px !important' },
      }}
    >
      <CourseStudentsTable
        rows={students}
        paper={false}
        theme={theme}
        translate={translate}
        dialogType={type}
        columns={COLUMNS_BY_TYPE[type]}
        pagination={true}
        paginationMode='server'
        totalCount={data?.students?.totalCount ?? 0}
        paginationModel={paginationModel}
        onPaginationModelChange={handlePaginationModelChange}
        showFooter={true}
        onStudentAction={handleStudentAction}
        noRowsMessage={translate('manage.courseStudents.noStudents')}
        isLoading={isLoading}
        sortable={false}
      />

      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {menuRow.current?.canSetDates
          ? [
              <MenuItem
                key='set-due-date'
                onClick={() => {
                  setDateDialog({ mode: 'dueDate', row: menuRow.current! });
                  handleMenuClose();
                }}
              >
                {translate('manage.setEnrolmentDate.setDueDate')}
              </MenuItem>,
              <MenuItem
                key='set-expiry-date'
                onClick={() => {
                  setDateDialog({ mode: 'expiry', row: menuRow.current! });
                  handleMenuClose();
                }}
              >
                {translate('manage.setEnrolmentDate.setExpiryDate')}
              </MenuItem>,
            ]
          : null}
        <MenuItem
          onClick={() => {
            const row = menuRow.current;
            handleMenuClose();
            setHistoryRow(row);
            onLearningHistory?.(row!);
          }}
        >
          {translate('manage.courseStudents.learningHistory')}
        </MenuItem>
        {showViewCertificate ? (
          <MenuItem
            onClick={() => {
              const row = menuRow.current;
              const sku = data?.courseReferenceCode;
              handleMenuClose();
              if (!row || !sku) return;
              openStudentCertificatePdf({
                employeeId: row.employeeId,
                sku,
                enrollmentId: row.learnUponEnrollmentId,
                instance: row.learnUponInstance,
                onProcessing: () =>
                  showSnackbar(translate('myKlayo.certificateProcessing'), 'info'),
                onError: () => showSnackbar(translate('myKlayo.certificateUnavailable'), 'error'),
              });
            }}
          >
            {translate('manage.courseStudents.viewCertificate')}
          </MenuItem>
        ) : null}

        {isAllowReminder ? (
          <MenuItem
            onClick={() => {
              setReminderRow(menuRow.current);
              handleMenuClose();
            }}
          >
            {translate('manage.courseStudents.sendReminder')}
          </MenuItem>
        ) : null}
        {isAllowUnenrol ? (
          <MenuItem
            onClick={() => {
              setUnenrolRow(menuRow.current);
              handleMenuClose();
            }}
          >
            {translate('manage.courseStudents.unenrol')}
          </MenuItem>
        ) : null}
      </Menu>

      <SetEnrolmentDateDialog
        open={!!dateDialog}
        mode={dateDialog?.mode ?? 'dueDate'}
        studentName={dateDialog?.row.name}
        courseName={courseName}
        courseEnrollmentId={dateDialog?.row.courseEnrollmentId}
        dueDate={dateDialog?.row.dueDate}
        expiryDate={dateDialog?.row.courseAccessExpiresAt}
        theme={theme}
        onClose={() => setDateDialog(null)}
        onSuccess={() => {
          // A due-date change can flip overdue, so refetch every course-students query (this
          // dialog's list and the tab behind it) via the parent prefix, plus the Courses grid
          // whose per-course Overdue count is built from the same enrolments (same keys unenrol
          // invalidates below).
          queryClient.invalidateQueries({ queryKey: queryKeys.courseStudents.key });
          queryClient.invalidateQueries({ queryKey: queryKeys.trainingCourses.list() });
        }}
      />

      {unenrolRow && (
        <ConfirmUnenrolStudentDialog
          row={unenrolRow}
          courseName={courseName}
          theme={theme}
          onClose={() => setUnenrolRow(null)}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: queryKeys.courseStudents.list() });
            queryClient.invalidateQueries({ queryKey: queryKeys.trainingCourses.list() });
          }}
        />
      )}

      <LearningHistoryDialog
        open={!!historyRow}
        onClose={() => setHistoryRow(null)}
        studentName={historyRow?.name}
        courseName={courseName}
        courseEnrollmentId={historyRow?.courseEnrollmentId}
        employeeTrainingId={historyRow?.employeeTrainingId}
        learningPathEnrollmentId={historyRow?.learningPathEnrollmentId}
        integrationEnrollmentId={historyRow?.integrationEnrollmentId}
        theme={theme}
      />

      <SendReminderDialog
        open={!!reminderRow}
        employeeId={reminderRow?.employeeId}
        studentName={reminderRow?.name}
        courseId={courseId}
        courseName={courseName}
        theme={theme}
        onClose={() => setReminderRow(null)}
        onSuccess={() => onSendReminderSuccess?.(courseName)}
      />
    </Dialog>
  );
};
